import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import GalaSite from "@/pages/GalaSite";
import AdminLogin from "@/pages/AdminLogin";
import AdminCallback from "@/pages/AdminCallback";
import AdminLayout from "@/components/admin/AdminLayout";
import ProtectedAdmin from "@/components/admin/ProtectedAdmin";
import DashboardPage from "@/pages/admin/DashboardPage";
import SponsorsPage from "@/pages/admin/SponsorsPage";
import PartnersPage from "@/pages/admin/PartnersPage";
import NewsPage from "@/pages/admin/NewsPage";
import DocumentsPage from "@/pages/admin/DocumentsPage";
import MediaRoomPage from "@/pages/admin/MediaRoomPage";
import InboxPage from "@/pages/admin/InboxPage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import RunOfShowPage from "@/pages/admin/RunOfShowPage";
import ProfilePage from "@/pages/admin/ProfilePage";
import DonationsPage from "@/pages/admin/DonationsPage";
import UsersPage from "@/pages/admin/UsersPage";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

const TOAST_OPTIONS = {
  style: {
    background: "#1A1A1A",
    color: "#fff",
    border: "1px solid rgba(255,0,245,0.3)",
  },
};

function AppRouter() {
  const location = useLocation();
  // Intercept Emergent OAuth callback (URL fragment) BEFORE the normal routing runs.
  if (location.hash?.includes("session_id=")) {
    return <AdminCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<GalaSite />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedAdmin>
            <AdminLayout><DashboardPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/sponsors"
        element={
          <ProtectedAdmin>
            <AdminLayout><SponsorsPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedAdmin>
            <AdminLayout><PartnersPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/news"
        element={
          <ProtectedAdmin>
            <AdminLayout><NewsPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/documents"
        element={
          <ProtectedAdmin>
            <AdminLayout><DocumentsPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/media"
        element={
          <ProtectedAdmin>
            <AdminLayout><MediaRoomPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/inbox"
        element={
          <ProtectedAdmin>
            <AdminLayout><InboxPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedAdmin>
            <AdminLayout><AnalyticsPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/run-of-show"
        element={
          <ProtectedAdmin>
            <AdminLayout><RunOfShowPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedAdmin>
            <AdminLayout><ProfilePage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/donations"
        element={
          <ProtectedAdmin>
            <AdminLayout><DonationsPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedAdmin requireSuper>
            <AdminLayout><UsersPage /></AdminLayout>
          </ProtectedAdmin>
        }
      />
    </Routes>
  );
}

function App() {
  // The Emergent platform script overwrites <title> and meta description shortly
  // after page load. We re-assert AAWTA branding on every mount + via a short
  // interval so a late-arriving overwrite is corrected within ~1s.
  useEffect(() => {
    const TITLE = "AAWTA · Power to Drive Change Gala";
    const DESC = "The 3rd Annual Power to Drive Change Fundraising Gala — African American Women Trucking Association · Atlanta · December 10, 2026";
    const ICON = "https://customer-assets.emergentagent.com/job_e46e40bf-3320-41b9-b27d-8d7b60eb4a9e/artifacts/xp00bckt_statonary%20design%20AAWTA%20Logo_1-100%20%281%29%20%281%29.jpg";
    const apply = () => {
      if (document.title !== TITLE) document.title = TITLE;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && meta.content !== DESC) meta.content = DESC;
      let icon = document.querySelector('link[rel="icon"]');
      if (!icon) {
        icon = document.createElement("link");
        icon.rel = "icon";
        document.head.appendChild(icon);
      }
      if (icon.href !== ICON) icon.href = ICON;
    };
    apply();
    const id = setInterval(apply, 500);
    setTimeout(() => clearInterval(id), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={TOAST_OPTIONS}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
